/* @HEADER
 * @version 0.15.943 | 2026-07-15
 * @purpose Mobile left nav-drawer (ZVX-DEV-2531) — project/space switcher + Настройки/workspace/account footer. Overlay above the bottom bar (z-index 401), opened via burger/chip/edge-swipe, closed via scrim tap or swipe-left. Reuses BoardNav, WorkspaceSwitcher, UserMenu instead of rebuilding them. Carries no tool-destination links (those stay in the "Ещё" sheet).
 * @sidecar MobileNavDrawer.tsx.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-2531, ZVX-DEV-2337, ZVX-DEV-2533

import { useEffect, useRef } from 'react';
import { useMessages } from '../shared/i18n/useMessages';
import { mobileSelectors } from './mobile-selectors';
import { useEdgeSwipeDrawer, DRAWER_WIDTH_PX } from '../hooks/useEdgeSwipeDrawer';
import { BoardNav } from '../app/BoardNav';
import { WorkspaceSwitcher } from '../features/workspace/public-api';
import { UserMenu } from '../features/account/public-api';
import { SettingsIcon } from '../app/NavIcons';
import type { BoardGroupRecord } from '../lib/db';

export interface MobileNavDrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called by any trigger (burger, chip, ⋮, gesture) to open the drawer. */
  onOpen: () => void;
  /** Called on scrim tap, swipe-close, or Escape. */
  onClose: () => void;
  /** Whether the drawer's gesture listeners + rendering are active (mobile-only gate). */
  enabled: boolean;

  boardGroups: BoardGroupRecord[];
  activeBoardGroupId: string | null;
  onSelectBoardGroup?: (groupId: string) => void;
  onCreateBoardGroup?: (name: string, parentId: string | null) => void;
  onDeleteBoardGroup?: (groupId: string) => void;
  canCreateSpace: boolean;
  onSpaceLimitBlocked: () => void;

  onNavigateSettings: () => void;
  currentRoute: string;
  onOpenAccount?: () => void;
}

/**
 * Mobile-only left nav-drawer.
 *
 * - Overlay (not push): `translateX(-100%) -> 0`, width `min(85vw, 360px)`.
 * - Opens via: burger tap, project-switcher chip tap, [⋮] tap (all wired by
 *   the AppShell caller through `onOpen`), or left-edge swipe-right (bound
 *   internally via `useEdgeSwipeDrawer`).
 * - Closes via: scrim tap, swipe-left from inside the panel, Escape, or the
 *   Android back button (AppShell wires popstate to `onClose`).
 * - z-index: `--z-nav-drawer-scrim` (400) / `--z-nav-drawer` (401) — above
 *   `.mobile-bottom-bar` (200) and `.bottom-sheet` (300), so the footer
 *   (Настройки / WorkspaceSwitcher / account) is never clipped.
 * - Content is the CONTEXT subset only (project/space switcher + footer) —
 *   tool destinations (Структура/Wiki/События/Дневник/Аналитика/OKR) stay in
 *   the "Ещё" sheet; no duplication.
 */
export function MobileNavDrawer({
  open,
  onOpen,
  onClose,
  enabled,
  boardGroups,
  activeBoardGroupId,
  onSelectBoardGroup,
  onCreateBoardGroup,
  onDeleteBoardGroup,
  canCreateSpace,
  onSpaceLimitBlocked,
  onNavigateSettings,
  currentRoute,
  onOpenAccount,
}: MobileNavDrawerProps) {
  const m = useMessages();
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const { dragTranslateX, dragScrimOpacity, isDragging, panelRef } = useEdgeSwipeDrawer({
    open,
    onOpen,
    onClose,
    enabled,
    width: DRAWER_WIDTH_PX,
  });

  // Focus management: trap focus while open, restore on close (ZVX-DEV-2531 a11y).
  useEffect(() => {
    if (!enabled) return;
    if (open) {
      restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      const panel = panelRef.current;
      const first = panel?.querySelector<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      );
      (first ?? panel)?.focus();
    } else {
      restoreFocusRef.current?.focus?.();
      restoreFocusRef.current = null;
    }
  }, [open, enabled, panelRef]);

  // Escape closes; Tab/Shift+Tab cycle only within the panel while open.
  useEffect(() => {
    if (!enabled || !open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])'),
      ).filter(el => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, open, onClose, panelRef]);

  // Android/browser back button closes the drawer instead of navigating away.
  useEffect(() => {
    if (!enabled || !open) return;
    history.pushState({ mobileNavDrawer: true }, '');
    const onPopState = () => onClose();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, open]);

  if (!enabled) return null;

  const dragging = isDragging && dragTranslateX !== null;
  const panelStyle = dragging ? { transform: `translateX(${dragTranslateX}px)`, transition: 'none' } : undefined;
  const scrimStyle = dragging ? { opacity: dragScrimOpacity ?? 0, transition: 'none' } : undefined;

  return (
    <>
      {/* ZVX-DEV-2744 (BUG 2): left-edge swipe-catcher — only mounted while
          closed. `touch-action: none` (mobile.css) stops the browser from
          hijacking the horizontal drag as a scroll and firing pointercancel
          before useEdgeSwipeDrawer's gesture completes. */}
      {!open && (
        <div
          className="mobile-nav-drawer-edge-catcher"
          data-testid={mobileSelectors.mobileNavDrawerEdgeCatcher}
          aria-hidden="true"
        />
      )}
      <div
        className="mobile-nav-drawer-scrim"
        data-open={open ? 'true' : 'false'}
        data-testid={mobileSelectors.mobileNavDrawerScrim}
        aria-hidden="true"
        style={scrimStyle}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={m.mobile.nav.drawer.label}
        className="mobile-nav-drawer"
        data-open={open ? 'true' : 'false'}
        data-testid={mobileSelectors.mobileNavDrawer}
        style={panelStyle}
        tabIndex={-1}
      >
        <div className="mobile-nav-drawer__scroll">
          <div className="mobile-nav-drawer__nav">
            <BoardNav
              groups={boardGroups}
              collapsed={false}
              activeGroupId={activeBoardGroupId}
              onSelectGroup={(groupId) => { onSelectBoardGroup?.(groupId); onClose(); }}
              onCreateGroup={(name, parentId) => onCreateBoardGroup?.(name, parentId)}
              onDeleteGroup={(groupId) => onDeleteBoardGroup?.(groupId)}
              canCreateSpace={canCreateSpace}
              onSpaceLimitBlocked={onSpaceLimitBlocked}
            />
          </div>

          <div className="mobile-nav-drawer__footer">
            <button
              type="button"
              className="mobile-nav-drawer__footer-row mobile-nav-drawer__settings"
              data-testid={mobileSelectors.mobileNavDrawerSettings}
              aria-label={m.mobile.nav.drawer.settings}
              onClick={() => { onNavigateSettings(); onClose(); }}
              data-active={currentRoute === 'settings' ? 'true' : 'false'}
            >
              <span className="mobile-nav-drawer__footer-icon" aria-hidden="true"><SettingsIcon /></span>
              <span className="mobile-nav-drawer__footer-label">{m.mobile.nav.drawer.settings}</span>
            </button>

            <div
              className="mobile-nav-drawer__footer-row"
              data-testid={mobileSelectors.mobileNavDrawerWorkspaceSwitcher}
            >
              <WorkspaceSwitcher collapsed={false} />
            </div>

            <div
              className="mobile-nav-drawer__footer-row"
              data-testid={mobileSelectors.mobileNavDrawerAccount}
            >
              <UserMenu
                collapsed={false}
                onOpenAccount={() => { onOpenAccount?.(); onClose(); }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
